import { Link } from "react-router-dom";
import ScholarOneLogin from "../../assets/mc-login-page.jpg";
import ScholarOneHome from "../../assets/mc-home-page.jpg";
import CoauthorSupportedQueues from "../../assets/coauthor-supported-queues.jpg";
import ManuscriptsWithDecisionsPage from "../../assets/manuscripts-with-decisions-page.jpg";
import CoauthorDashboardLoggedOut from "../../assets/coauthor-dashboard-loggedout.jpg";
import CoauthorMyStatsPage from "../../assets/coauthor-my-stats-page.png";
import styles from "./Guide.module.css";

const Guide = () => {
  return (
    <div className={styles["guide-page"]}>
      <nav className={styles.sidebar}>
        <ul>
          <li>
            <a href="#getting-started">Getting Started</a>
            <p className={styles.sidebarSubsection}>
              <a className={styles.sidebarSubsectionItem} href="#installation">
                Installation
              </a>
              <a className={styles.sidebarSubsectionItem} href="#how-to-use">
                Usage
              </a>
            </p>
          </li>
          <li>
            <a href="#faq">FAQ</a>
          </li>
        </ul>
      </nav>

      <main className={styles.content}>
        <section className={styles.section}>
          <h1 className={styles.sectionTitle}>Welcome to Coauthor</h1>
          <p>
            The mission of the Coauthor Project is to increase transparency in
            scientific publishing using community-sourced manuscript peer review
            data. The Coauthor browser extension provides a free, automated way
            to contribute your manuscript submission data. In just a few clicks,
            you can contribute your entire manuscript submission history at a
            journal - no tedious form filling required. Interested in
            contributing? Follow the instructions below to get started.
          </p>
        </section>
        <section id="getting-started" className={styles.section}>
          <h1>Getting Started</h1>
          <p>
            Before starting we strongly recommend reading the{" "}
            <a href="#faq">FAQ</a> to understand what data Coauthor collects
            from you, so you can make an informed decision whether or not to
            contribute your data.
          </p>
          <h2 id="installation" className={styles.subsectionTitle}>
            Installation
          </h2>
          <p>
            Currently, Coauthor is only supported on Chromium-based browsers,
            and non-Chromium-based browsers, like Safari and Firefox, are not
            supported. We have tested Coauthor on the following popular
            Chromium-based browsers:
          </p>
          <ul className={styles.bulletedList}>
            <li>Chrome</li>
            <li>Edge</li>
            <li>Brave</li>
            <li>Opera</li>
          </ul>
          <p>
            If you would like us to support other popular browsers, please let
            us know at maffielab@gmail.com.
          </p>
          <p>
            To install the Coauthor extension on Chromium browsers, follow these
            simple steps:
          </p>
          <ol className={styles.stepContainer}>
            <li className={styles.step}>
              <span className={styles.stepIcon}>1</span>
              <span className={styles.stepContent}>
                Go to the{" "}
                <a
                  href="https://chromewebstore.google.com/detail/coauthor/ahajebjbfifafhoifinahkeifcgfpocl"
                  target="_blank"
                  rel="noreferrer"
                >
                  Coauthor Chrome Web Store page.
                </a>
              </span>
            </li>
            <li className={styles.step}>
              <span className={styles.stepIcon}>2</span>
              <span className={styles.stepContent}>
                Click <b>Add to Chrome</b>.
              </span>
            </li>
            <li className={styles.step}>
              <span className={styles.stepIcon}>3</span>
              <span className={styles.stepContent}>
                Then, click <b>Add extension</b>.
              </span>
            </li>
            <li className={styles.step}>
              <span className={styles.stepIcon}>4</span>
              <span className={styles.stepContent}>
                <b>Success!</b> You should see a popup that says &quot;Coauthor
                has been installed&quot;.
              </span>
            </li>
          </ol>
          <h2 id="how-to-use" className={styles.subsectionTitle}>
            Usage
          </h2>
          <p>
            The browser extension can collect manuscript data from any journal
            submission site that uses the{" "}
            <b>ScholarOne Manuscripts (also known as Manuscript Central)</b>{" "}
            manuscript submission system. The login page for any ScholarOne site
            should look similar to this: <br />
            <img src={ScholarOneLogin} alt="ScholarOne Login" /> If the
            extension is working, a button will appear on the top right of the
            page: clicking this will open the Coauthor dashboard, where you can
            login and upload manuscripts.
          </p>
          <h3>Uploading Data</h3>
          <ol className={styles.stepContainer}>
            <li className={styles.step}>
              <span className={styles.stepIcon}>1</span>
              <span className={styles.stepContent}>
                Login to any ScholarOne journal submission site.
              </span>
            </li>
            <li className={styles.step}>
              <span className={styles.stepIcon}>2</span>
              <span className={styles.stepContent}>
                After logging in, go to your author dashboard: <br />
                <img src={ScholarOneHome} alt="ScholarOne Home" />
              </span>
            </li>
            <li className={styles.step}>
              <span className={styles.stepIcon}>3</span>
              <span className={styles.stepContent}>
                On your Author Dashboard there should be a panel on the left
                with queues that allow you to view and manage your manuscripts.
                Not all queues may appear; it depends on what manuscripts
                you&apos;ve submitted and where they are in the review process.
                Coauthor will only collect data from &quot;Manuscripts with
                Decisions&quot; and &quot;Manuscripts I have Co-Authored&quot;:{" "}
                <img
                  src={CoauthorSupportedQueues}
                  alt="Coauthor Supported Queues"
                />{" "}
                <br />
                If you do not see either of these queues, Coauthor cannot
                collect any data from this journal.
              </span>
            </li>
            <li className={styles.step}>
              <span className={styles.stepIcon}>4</span>
              <span className={styles.stepContent}>
                Click either one of the aforementioned queues. The page should
                now look like this:{" "}
                <img
                  src={ManuscriptsWithDecisionsPage}
                  alt="Coauthor Dashboard"
                />
                <br />
                By default, ScholarOne only displays your first ten submissions.{" "}
                <b>Please change the items per page</b> to fifty to help us
                create as reliable of a dataset as possible.
              </span>
            </li>
            <li className={styles.step}>
              <span className={styles.stepIcon}>5</span>
              <span className={styles.stepContent}>
                Open the Coauthor Dashboard by clicking the button in the top
                right. You will see a list of manuscripts on the page that are
                eligible for uploading (only manuscripts with known decisions).
                You must login with ORCID before you can upload manuscripts.{" "}
                <img
                  src={CoauthorDashboardLoggedOut}
                  alt="Coauthor Dashboard Logged Out"
                />
              </span>
            </li>
            <li className={styles.step}>
              <span className={styles.stepIcon}>6</span>
              <span className={styles.stepContent}>
                Once logged in, just click <b>Upload Manuscript Data</b> and
                you&apos;re done!
              </span>
            </li>
            <li className={styles.step}>
              <span className={styles.stepIcon}>7</span>
              <span className={styles.stepContent}>
                Please repeat this process for other journals. It will help us
                create more reliable information for the Coauthor community.
              </span>
            </li>
            <li className={styles.step}>
              <span className={styles.stepIcon}>8</span>
              <span className={styles.stepContent}>
                Visit{" "}
                <a
                  href="https://coauthor.fly.dev"
                  target="_blank"
                  rel="noreferrer"
                >
                  coauthor.fly.dev
                </a>{" "}
                where you can see overall journal statistics as well as your own
                personalized statistics. If you have signed in through the
                extension, you can view your personalized statistics by going to{" "}
                <Link to="/dashboard" target="_blank" rel="noreferrer">
                  My Stats
                </Link>
                : <br />
                <img src={CoauthorMyStatsPage} alt="Coauthor My Stats Page" />
              </span>
            </li>
          </ol>
          <span>Thank you for contributing to the Coauthor Project!</span>
        </section>

        <section id="faq" className={styles.section}>
          <h1>Frequently Asked Questions</h1>
          <h2>What data does Coauthor collect?</h2>
          <h3>Manuscript Peer Review Metadata</h3>
          <p>
            Coauthor collects non-sensitive data that academic journals
            communicate to you during the manuscript peer review process. This
            information is collected by automatically scraping the webpage when
            you navigate to your author dashboard. For each manuscript draft,
            the following information is collected:
          </p>
          <ul className={styles.bulletedList}>
            <li>Submission date</li>
            <li>Decision date</li>
            <li>Decision (e.g., Major Revision, Reject, Accept)</li>
            <li>Manuscript ID</li>
          </ul>
          <p>
            Manuscript data is aggregated into a dataset of academic
            journals&apos; publishing statistics. The dataset does not reveal
            any personally identifiable information, and is publicly available
            at{" "}
            <a
              href="https://coauthor.fly.dev/journals"
              target="_blank"
              rel="noreferrer"
            >
              coauthor.fly.dev/journals
            </a>{" "}
            .
          </p>
          <h3>Authentication Information</h3>
          <p>
            To provide user-specific features and combat fraud, Coauthor
            requires you to sign in with{" "}
            <a href="https://orcid.org/" target="_blank" rel="noreferrer">
              ORCID{" "}
            </a>{" "}
            before uploading manuscript information. We store your publicly
            available ORCID iD to uniquely identify you, and no other
            information is stored.
          </p>
          <h3>Error and Crash Information</h3>
          <p>
            In the event of an error, Coauthor collects and transmits
            error-related information (e.g., that an error occurred, what the
            error was) but does not collect any personally identifiable
            information.
          </p>
          <h2>Does Coauthor share user data with any other parties?</h2>
          <p>
            Coauthor does not share user data with any other parties and will
            never sell user data.
          </p>
          <h2>How can I report an issue with Coauthor?</h2>
          <p>
            If you have any issues with Coauthor, please let us know at{" "}
            <a href="mailto:maffielab@gmail.com">maffielab@gmail.com</a>.
          </p>
        </section>
      </main>
    </div>
  );
};

export default Guide;
